import "./polyfill-react.js";
import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { tanstackRouterBrowserTracingIntegration } from "@sentry/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { PageLoader } from "./components/ui/PageLoader";
import { Button } from "./components/ui/Button";
import { setupNavTransitionClasses } from "./lib/view-transition-nav";

import "./styles/tokens.css";
import "./styles/globals.css";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN || process.env.SENTRY_DSN;
const isProduction = import.meta.env.PROD || process.env.NODE_ENV !== "development";
const appVersion = import.meta.env.VITE_APP_VERSION || "dev";

const router = createRouter({
  routeTree,
  defaultPendingComponent: PageLoader,
  defaultViewTransition: true,
});

setupNavTransitionClasses(router);

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || (isProduction ? 'production' : 'development'),
    release: `kraphene-web@${appVersion}`,
    integrations: [
      tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: isProduction ? 0.2 : 0.05,
    replaysSessionSampleRate: isProduction ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      const error = hint.originalException;

      if (error instanceof Error) {
        // Skip routine client-side API errors (401, 403, 404, 422), but NEVER skip 5xx server errors or network failures
        const apiStatus = (error as Error & { status?: number }).status;
        if (
          (error.name === "ApiError" || error.name === "ApiErrorResponse") &&
          apiStatus && apiStatus >= 400 && apiStatus < 500 && apiStatus !== 408 && apiStatus !== 429
        ) {
          return null;
        }
        if (error.message.includes("ResizeObserver loop")) {
          return null;
        }
        if (error.message.includes("Non-Error promise rejection captured")) {
          return null;
        }
        if (
          error.message.includes("Extension") ||
          error.message.includes("extension") ||
          (error as Error & { stack?: string }).stack?.includes("chrome-extension://") ||
          (error as Error & { stack?: string }).stack?.includes("moz-extension://") ||
          (error as Error & { stack?: string }).stack?.includes("safari-extension://")
        ) {
          return null;
        }
      }

      return event;
    },
  });

  // Intercept console.error to ensure all logged Error objects get reported to Sentry
  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    originalConsoleError(...args);
    for (const arg of args) {
      if (arg instanceof Error) {
        Sentry.captureException(arg);
        break;
      }
    }
  };
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught runtime error:", error, errorInfo);
    if (typeof Sentry !== "undefined") {
      Sentry.captureException(error, { extra: { errorInfo } });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", backgroundColor: "var(--surface-1, #111413)", color: "var(--text-primary, #F9F9F7)", fontFamily: "system-ui, sans-serif", padding: "2rem", textAlign: "center" }}>
          <h1 style={{ fontSize: "2rem", marginBottom: "1rem", color: "var(--danger-500, #B85555)" }}>Application Error</h1>
          <p style={{ marginBottom: "2rem", color: "var(--text-secondary, #A0A0A0)", maxWidth: "500px" }}>We encountered an unexpected error. Our team has been notified. Please try refreshing the page.</p>
          <div style={{ background: "var(--surface-2, #1A1C1B)", padding: "1rem", borderRadius: "8px", overflowX: "auto", maxWidth: "800px", fontSize: "0.875rem", color: "var(--text-muted, #737373)", textAlign: "left", whiteSpace: "pre-wrap" }}>
            {this.state.error?.toString()}
          </div>
          <Button variant="primary" size="lg" onClick={() => window.location.reload()}>
            Reload Application
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

const root = document.getElementById("root");

if (!root) {
  throw new Error("Root element not found");
}

createRoot(root).render(
  <React.StrictMode>
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </React.StrictMode>
);
